import { useEffect, useRef, useCallback } from "react";
import { hasRole } from "../lib/roles";
import {
  getFirestore,
  collection,
} from "firebase/firestore";
import { safeOnSnapshot } from "../lib/safeSnapshot";
import { useAuth } from "../contexts/AuthContext";
import { useNotifications } from "../contexts/NotificationContext";

export const useMasterNotifications = () => {
  const { user, loading: authLoading } = useAuth();
  const {
    updateMasterUnreadCount,
    updateBudgetCount,
    updateApprovalCount,
    updateApprovedCount,
    updateInAnalysisCount,
    updateOnGoingCount,
    updatePaymentsCount,
    updateRefundsCount,
  } = useNotifications();

  // NOTA: A contagem de "Todos Projetos" (masterUnreadCount) é feita automaticamente
  // apenas para as coleções que aparecem na aba "Todos Projetos", evitando contagem incorreta

  // Referencias para evitar re-renders desnecessários
  const countersRef = useRef({
    unread: new Set(),
    budget: new Set(),
    approval: new Set(),
    approved: new Set(),
    inAnalysis: new Set(),
    onGoing: new Set(),
  });

  const unsubscribeFunctionsRef = useRef([]);
  const isInitializedRef = useRef(false);
  const timeoutRef = useRef(null);

  // Debounce para atualizações
  const updateCounters = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      const counters = countersRef.current;
      updateMasterUnreadCount(counters.unread.size);
      updateBudgetCount(counters.budget.size);
      updateApprovalCount(counters.approval.size);
      updateApprovedCount(counters.approved.size);
      updateInAnalysisCount(counters.inAnalysis.size);
      updateOnGoingCount(counters.onGoing.size);
      updatePaymentsCount(counters.payments.size);
      updateRefundsCount(counters.refunds.size);
    }, 100); // Debounce de 100ms
  }, [
    updateMasterUnreadCount,
    updateBudgetCount,
    updateApprovalCount,
    updateApprovedCount,
    updateInAnalysisCount,
    updateOnGoingCount,
    updatePaymentsCount,
    updateRefundsCount,
  ]);

  useEffect(() => {
    if (authLoading || !user || isInitializedRef.current) {
      return;
    }

    // Só executar para usuário master (cobre userType string OU array).
    if (!hasRole(user?.userType, "master")) {
      return;
    }

    isInitializedRef.current = true;

    const fetchAllMasterNotifications = async () => {
      try {
        const firestore = getFirestore();

        // Coleções que compõem a aba "Todos Projetos" do master — mesma lista
        // usada por MasterProjects.js / MasterOnGoing.js / MasterPayments.js.
        const MASTER_PROJECT_COLLECTIONS = [
          "b2bprojects",
          "b2bprojectspaid",
          "b2bapproved",
          "b2bdocprojects",
          "b2bapproval",
          "b2cprojectspaid",
          "b2cdocprojects",
          "b2capproval",
        ];

        // Lê um status que pode vir como string OU objeto { status }.
        const statusStr = (v) =>
          String((v && typeof v === "object" ? v.status : v) || "");

        // Cada contador = coleções monitoradas + predicado que decide se o doc
        // entra na conta. Os predicados espelham EXATAMENTE o filtro da tabela
        // de cada página, para o badge do sidebar bater com a tela.
        const COUNTER_DEFS = [
          // Aguardando Orçamento — todos os docs (ProjectsBudget.js).
          {
            name: "budget",
            cols: ["b2bdocprojects", "b2cdocprojects"],
            test: () => true,
          },
          // Aguardando Aprovação — exclui cancelados (ProjectsApproval.js).
          {
            name: "approval",
            cols: ["b2bapproval", "b2capproval"],
            test: (d) =>
              !statusStr(d.project_status).toLowerCase().includes("cancel"),
          },
          // Aprovados — todos os docs (contador auxiliar).
          {
            name: "approved",
            cols: ["b2bapproved", "b2capproved"],
            test: () => true,
          },
          // Em Análise — project_status "em análise" (ProjectsInAnalysis.js).
          {
            name: "inAnalysis",
            cols: ["b2bapproved", "b2bprojectspaid", "b2cprojectspaid"],
            test: (d) =>
              statusStr(d.project_status).toLowerCase() === "em análise",
          },
          // Em Andamento (MasterOnGoing.js).
          {
            name: "onGoing",
            cols: MASTER_PROJECT_COLLECTIONS,
            test: (d) => d.project_status === "Em Andamento",
          },
          // Pagamentos — Pendente ou Divergência (MasterPayments.js).
          {
            name: "payments",
            cols: MASTER_PROJECT_COLLECTIONS,
            test: (d) => {
              const s = statusStr(d.payment_status);
              return s === "Pendente" || s === "Divergência";
            },
          },
          // Reembolsos — payment_status "Em Reembolso" (MasterRefund.js).
          {
            name: "refunds",
            cols: ["b2bprojectspaid", "b2cprojectspaid"],
            test: (d) => statusStr(d.payment_status) === "Em Reembolso",
          },
        ];

        // União das coleções a escutar: UM listener por coleção alimenta todos
        // os contadores (bem menos leituras que um listener por contador).
        const MONITORED = Array.from(
          new Set([
            ...MASTER_PROJECT_COLLECTIONS,
            ...COUNTER_DEFS.flatMap((d) => d.cols),
          ])
        );

        // Reset dos contadores.
        countersRef.current = {
          unread: new Set(),
          budget: new Set(),
          approval: new Set(),
          approved: new Set(),
          inAnalysis: new Set(),
          onGoing: new Set(),
          payments: new Set(),
          refunds: new Set(),
        };

        // Contribuição de cada coleção para cada contador. O total é a UNIÃO
        // dessas contribuições, recomputada a cada snapshot — assim, quando um
        // doc sai do filtro (ex.: deixa de estar "Em Análise"), seu id some do
        // total em vez de ficar preso para sempre (bug do "1 a mais").
        const contrib = { unread: {} };
        COUNTER_DEFS.forEach((d) => {
          contrib[d.name] = {};
        });
        const recomputeUnion = (name) => {
          const union = new Set();
          Object.values(contrib[name]).forEach((set) =>
            set.forEach((id) => union.add(id))
          );
          countersRef.current[name] = union;
        };

        MONITORED.forEach((col) => {
          const unsubscribe = safeOnSnapshot(
            collection(firestore, col),
            (snapshot) => {
              const docs = snapshot.docs;

              // "Todos Projetos" (não lidos): só recomputa fora da própria
              // página, para não zerar enquanto o master abre os projetos.
              if (MASTER_PROJECT_COLLECTIONS.includes(col)) {
                const isOnProjectsPage =
                  window.location.pathname === "/company/master/projects" ||
                  window.location.pathname === "/company/master";
                if (!isOnProjectsPage) {
                  const s = new Set();
                  docs.forEach((doc) => {
                    if (doc.data().MasterRead === false) s.add(doc.id);
                  });
                  contrib.unread[col] = s;
                  recomputeUnion("unread");
                }
              }

              // Demais contadores.
              COUNTER_DEFS.forEach((def) => {
                if (!def.cols.includes(col)) return;
                const s = new Set();
                docs.forEach((doc) => {
                  if (def.test(doc.data())) s.add(doc.id);
                });
                contrib[def.name][col] = s;
                recomputeUnion(def.name);
              });

              updateCounters();
            },
            { label: col }
          );

          unsubscribeFunctionsRef.current.push(unsubscribe);
        });

        console.log("✅ Master notifications initialized successfully");
      } catch (error) {
        console.error("❌ Erro ao buscar notificações do master:", error);
      }
    };

    fetchAllMasterNotifications();

    // Cleanup function
    return () => {
      unsubscribeFunctionsRef.current.forEach((unsubscribe) => {
        if (unsubscribe) unsubscribe();
      });
      unsubscribeFunctionsRef.current = [];
      isInitializedRef.current = false;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);
};
